/**
 * Shared performance-metrics processing for the dashboard home cards
 * (Creator + Organization PerformanceOverviewCard).
 *
 * Pure functions only — fetching lives in each page's .duck.js. Given the
 * raw inputs (transactions, audience lists, GA4 page-view blob) and a day
 * range, this produces the exact `metrics` shape PerformanceOverviewCard
 * consumes: { totalSubscribers, totalRevenue, totalViews, percentChange,
 * startSubscribers, startRevenue, startViews, daily }.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

const sumPayout = txns =>
  txns
    .filter(t => t?.attributes?.payoutTotal)
    .reduce((sum, t) => sum + t.attributes.payoutTotal.amount, 0);

const computeChange = (curr, prev) => {
  if (prev === 0) return curr > 0 ? 100 : 0;
  return Math.round(((curr - prev) / prev) * 100);
};

const countMembers = audienceLists =>
  (audienceLists || []).reduce(
    (sum, list) => sum + (list.memberCount || list.members?.length || 0),
    0
  );

/**
 * Build the daily time series for the chart. Combines transaction revenue
 * (cumulative within the window), GA4 views-by-date (cumulative), and a
 * linear subscriber-growth approximation to the current total.
 */
export function buildDailyTimeSeries(transactions, viewsByDate, totalSubscribers, rangeDays) {
  const now = new Date();
  const points = rangeDays <= 7 ? 7 : rangeDays <= 30 ? 5 : rangeDays <= 90 ? 5 : 6;
  const interval = Math.floor(rangeDays / (points - 1));
  const rangeStart = new Date(now.getTime() - rangeDays * DAY_MS);
  const rangeStartKey = rangeStart.toISOString().slice(0, 10);
  const result = [];

  for (let i = 0; i < points; i++) {
    const daysBack = rangeDays - i * interval;
    const pointDate = new Date(now.getTime() - daysBack * DAY_MS);
    const dateLabel = pointDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const dateKey = pointDate.toISOString().slice(0, 10);

    const revenueCumulative = transactions
      .filter(t => {
        const d = new Date(t.attributes.createdAt);
        return d >= rangeStart && d <= pointDate;
      })
      .filter(t => t.attributes.payoutTotal)
      .reduce((sum, t) => sum + t.attributes.payoutTotal.amount, 0);

    let viewsCumulative = 0;
    for (const [date, data] of Object.entries(viewsByDate || {})) {
      if (date <= dateKey && date >= rangeStartKey) {
        viewsCumulative += data?.views || 0;
      }
    }

    const ratio = points > 1 ? i / (points - 1) : 1;
    const startRatio = 0.8;
    const subscribers = Math.round(totalSubscribers * (startRatio + (1 - startRatio) * ratio));

    result.push({ date: dateLabel, subscribers, revenue: revenueCumulative, views: viewsCumulative });
  }

  return result;
}

/**
 * Compute the full metrics object from already-fetched raw inputs for a
 * given day range. Revenue + views are windowed (revenue from the txns,
 * views from the GA4 window aggregate); total members is the current
 * audience snapshot.
 */
export function computePerformanceMetrics({
  transactions = [],
  audienceLists = [],
  pageViewData = {},
  rangeDays = 190,
}) {
  const totalSubscribers = countMembers(audienceLists);

  const now = new Date();
  const currentStart = new Date(now.getTime() - rangeDays * DAY_MS);
  const previousStart = new Date(currentStart.getTime() - rangeDays * DAY_MS);

  const currentTxns = transactions.filter(t => new Date(t.attributes.createdAt) >= currentStart);
  const previousTxns = transactions.filter(t => {
    const d = new Date(t.attributes.createdAt);
    return d >= previousStart && d < currentStart;
  });

  const totalRevenue = sumPayout(currentTxns);
  const previousRevenue = sumPayout(previousTxns);

  const totalViews = pageViewData?.totalViews || 0;
  const previousTotalViews = pageViewData?.previousTotalViews || 0;

  const revenueChange = computeChange(totalRevenue, previousRevenue);
  const viewsChange = computeChange(totalViews, previousTotalViews);
  // Revenue is the headline driver; fall back to views when there's no revenue.
  const percentChange = totalRevenue > 0 ? revenueChange : viewsChange;

  const daily = buildDailyTimeSeries(
    currentTxns,
    pageViewData?.viewsByDate || {},
    totalSubscribers,
    rangeDays
  );

  return {
    totalSubscribers,
    totalRevenue,
    totalViews,
    percentChange,
    startSubscribers: daily.length > 0 ? daily[0].subscribers : 0,
    startRevenue: daily.length > 0 ? daily[0].revenue : 0,
    startViews: daily.length > 0 ? daily[0].views : 0,
    daily,
  };
}
